import { AlertTriangle, Search, ShieldAlert, Tent, UserX } from "lucide-react";
import { type ChangeEvent, type ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { AccountActivityRow, RecipientClassification } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { DataTable } from "@/components/DataTable";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { MetricLabel } from "@/components/MetricTooltip";
import { PageHeader } from "@/components/PageHeader";
import { useScope } from "@/context/ScopeContext";
import { useAccountsAttention } from "@/hooks/queries";
import { formatInt, formatPct } from "@/lib/format";
import { isSyntheticRecipient } from "@/lib/names";

// Accounts requiring attention (R11) — the churn-risk / dormant / stale-pilot worklist from
// rpt_account_activity_flag. recipient→district (hence fleetCount) is INFERRED via
// rpt_recipient_match (matchMethod + confidence carried on every row); "no recent replacement" is a
// human risk flag for a worklist, not a definitive churn call. Synthetic/internal test rows are
// hidden per the names.ts convention — never deleted, just not surfaced as an account.

type AttentionFilter = "all" | "churnRisk" | "dormant" | "pilot";

const FILTER_OPTIONS: readonly { readonly value: AttentionFilter; readonly label: string }[] = [
  { value: "all", label: "All flagged" },
  { value: "churnRisk", label: "Churn-risk" },
  { value: "dormant", label: "Dormant" },
  { value: "pilot", label: "Stale pilot" },
];

const CLASSIFICATION_LABEL: Record<RecipientClassification, string> = {
  district: "District",
  customer: "Customer",
  one_off: "One-off",
  pilot: "Pilot",
  internal: "Internal",
};

type SortKey = "fleetCount" | "daysSinceReplacement" | "daysSinceOrder" | "confidence";

const SORT_OPTIONS: readonly { readonly value: SortKey; readonly label: string }[] = [
  { value: "fleetCount", label: "Fleet size" },
  { value: "daysSinceReplacement", label: "Days since replacement" },
  { value: "daysSinceOrder", label: "Days since order" },
  { value: "confidence", label: "Match confidence" },
];

// Unknown/never-replaced sorts to the bottom for the "days since" keys rather than looking like 0
// (a real 0-day replacement would otherwise tie with "we have no idea").
const NEVER_SENTINEL = -1;

// Confidence is stored 0..1 on the contract; render it as the 0..100 percentage used everywhere
// else a match/inference confidence is shown in the hub.
const CONFIDENCE_SCALE = 100;
const CONFIDENCE_DIGITS = 0;
const confidencePct = (c: number): string => formatPct(c * CONFIDENCE_SCALE, CONFIDENCE_DIGITS);

const sortValue = (row: AccountActivityRow, key: SortKey): number => {
  if (key === "fleetCount") {
    return row.fleetCount;
  }
  if (key === "daysSinceReplacement") {
    return row.daysSinceLastReplacement ?? NEVER_SENTINEL;
  }
  if (key === "daysSinceOrder") {
    return row.daysSinceLastOrder ?? NEVER_SENTINEL;
  }
  return row.confidence;
};

const matchesFilter = (row: AccountActivityRow, filter: AttentionFilter): boolean => {
  if (filter === "churnRisk") {
    return row.churnRisk;
  }
  if (filter === "dormant") {
    return row.dormant;
  }
  if (filter === "pilot") {
    return row.isPilot;
  }
  return true;
};

const FlagChips = ({ row }: { readonly row: AccountActivityRow }): ReactNode => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    {row.churnRisk ? (
      <span className="tag warn">
        <span className="dot" />
        Churn-risk
      </span>
    ) : null}
    {row.dormant ? (
      <span className="tag crit">
        <span className="dot" />
        Dormant
      </span>
    ) : null}
    {row.isPilot ? (
      <span className="tag ok">
        <span className="dot" />
        Pilot
      </span>
    ) : null}
    {!row.churnRisk && !row.dormant && !row.isPilot ? (
      <span style={{ fontSize: 11, color: "var(--br-text-muted)" }}>—</span>
    ) : null}
  </div>
);

export const AccountsAttentionPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useAccountsAttention();
  const { scope } = useScope();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<AttentionFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("fleetCount");

  const meta = data?.meta;
  const payload = data?.data;

  // Hide synthetic/internal test rows (per names.ts) before anything else touches the worklist —
  // never delete the data, just don't surface it as an account requiring human attention.
  const rows = useMemo(
    () => (payload?.rows ?? []).filter((r) => !isSyntheticRecipient(r.displayName)),
    [payload],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = rows
      .filter((r) => matchesFilter(r, filter))
      .filter((r) => needle.length === 0 || r.displayName.toLowerCase().includes(needle));
    return filtered.slice().sort((a, b) => sortValue(b, sortKey) - sortValue(a, sortKey));
  }, [rows, query, filter, sortKey]);

  const openRow = (row: AccountActivityRow): void => {
    const level = row.districtId ? "district" : "account";
    const id = row.districtId ?? row.recipientId;
    navigate(`/recipient/${encodeURIComponent(id)}?level=${level}`);
  };

  if (isError) {
    return (
      <>
        <PageHeader title="Accounts requiring attention" meta={meta} />
        <ChartCard title="Accounts requiring attention">
          <EmptyState
            title="Couldn't load accounts attention"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Accounts requiring attention"
        description="Churn-risk, dormant, and stale-pilot accounts — a human worklist, not a definitive churn call."
        meta={meta}
      />

      {payload ? (
        <InsightCallout
          headline={`${formatInt(payload.churnRiskCount)} accounts carry fleet with no recent replacement activity — ${formatInt(payload.accountsWithFleet)} accounts hold devices in total.`}
          stats={[
            { label: "Accounts with fleet", value: formatInt(payload.accountsWithFleet) },
            { label: "Churn-risk", value: formatInt(payload.churnRiskCount) },
            { label: "Dormant", value: formatInt(payload.dormantCount) },
            { label: "Stale pilots", value: formatInt(payload.pilotCount) },
          ]}
          severity="warn"
          icon={ShieldAlert}
        />
      ) : null}

      <div className="grid kpi-band">
        <Drillable
          metricKey="fleet.accountMatchRatePct"
          scope={scope}
          label="Drill into account match coverage"
        >
          <KpiCard
            label={
              <MetricLabel metricKey="fleet.accountMatchRatePct" label="Accounts with fleet" />
            }
            value={formatInt(payload?.accountsWithFleet ?? 0)}
            icon={ShieldAlert}
            source="accounts.attention"
            loading={isLoading}
          />
        </Drillable>
        <KpiCard
          label="Churn-risk"
          value={formatInt(payload?.churnRiskCount ?? 0)}
          icon={AlertTriangle}
          source="accounts.attention (fleet, no recent replacement)"
          loading={isLoading}
        />
        <KpiCard
          label="Dormant"
          value={formatInt(payload?.dormantCount ?? 0)}
          icon={UserX}
          source="accounts.attention (fleet, no recent order)"
          loading={isLoading}
        />
        <KpiCard
          label="Stale pilots"
          value={formatInt(payload?.pilotCount ?? 0)}
          icon={Tent}
          source="accounts.attention (classification = pilot)"
          loading={isLoading}
        />
      </div>

      <ChartCard
        title="Worklist"
        subtitle="Sortable, human-name-first. Click a row to open its recipient/district page."
        source="accounts.attention.rows"
        loading={isLoading}
        action={
          payload ? (
            <span
              className="br-tabular-nums"
              style={{ fontSize: 12, color: "var(--br-text-muted)" }}
            >
              {formatInt(visible.length)} of {formatInt(rows.length)}
            </span>
          ) : undefined
        }
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 360 }}>
            <Search
              size={15}
              aria-hidden
              style={{
                position: "absolute",
                top: "50%",
                left: 10,
                transform: "translateY(-50%)",
                color: "var(--br-text-muted)",
                pointerEvents: "none",
              }}
            />
            <input
              type="search"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder="Search accounts by name…"
              aria-label="Search accounts by name"
              style={{
                width: "100%",
                padding: "7px 12px 7px 32px",
                fontSize: 13,
                color: "var(--br-text-primary)",
                background: "var(--br-surface-2)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-sm)",
              }}
            />
          </div>

          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--br-text-muted)",
              }}
            >
              Flag
            </span>
            <select
              value={filter}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setFilter(e.target.value as AttentionFilter)
              }
              style={{
                padding: "6px 10px",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--br-text-primary)",
                cursor: "pointer",
                background: "var(--br-surface-2)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-sm)",
              }}
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--br-text-muted)",
              }}
            >
              Sort by
            </span>
            <select
              value={sortKey}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setSortKey(e.target.value as SortKey)
              }
              style={{
                padding: "6px 10px",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--br-text-primary)",
                cursor: "pointer",
                background: "var(--br-surface-2)",
                border: "1px solid var(--br-border)",
                borderRadius: "var(--br-radius-sm)",
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <DataTable<AccountActivityRow>
          rows={visible}
          rowKey={(r) => r.recipientId}
          onRowClick={openRow}
          emptyTitle="No accounts match"
          emptyMessage="Try a different search or flag filter."
          columns={[
            {
              key: "name",
              header: "Account",
              render: (r) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>{r.displayName}</span>
                  <span className="mono" style={{ fontSize: 11, color: "var(--br-text-muted)" }}>
                    {r.recipientId}
                  </span>
                </div>
              ),
            },
            {
              key: "classification",
              header: "Classification",
              render: (r) => <span>{CLASSIFICATION_LABEL[r.classification]}</span>,
            },
            {
              key: "fleet",
              header: "Fleet",
              align: "right",
              render: (r) => (
                <span className="mono br-tabular-nums">{formatInt(r.fleetCount)}</span>
              ),
            },
            {
              key: "daysSinceReplacement",
              header: "Days since replacement",
              align: "right",
              render: (r) => (
                <span className="mono br-tabular-nums">
                  {r.daysSinceLastReplacement === null
                    ? "never"
                    : formatInt(r.daysSinceLastReplacement)}
                </span>
              ),
            },
            {
              key: "daysSinceOrder",
              header: "Days since order",
              align: "right",
              render: (r) => (
                <span className="mono br-tabular-nums">
                  {r.daysSinceLastOrder === null ? "never" : formatInt(r.daysSinceLastOrder)}
                </span>
              ),
            },
            {
              key: "flags",
              header: "Flags",
              render: (r) => <FlagChips row={r} />,
            },
            {
              key: "match",
              header: "Match",
              align: "right",
              render: (r) => (
                <span
                  className="mono br-tabular-nums"
                  style={{ fontSize: 11, color: "var(--br-text-muted)" }}
                  title={`${r.matchMethod} match, ${confidencePct(r.confidence)} confidence`}
                >
                  {r.matchMethod} · {confidencePct(r.confidence)}
                </span>
              ),
            },
          ]}
        />

        {payload ? (
          <p style={{ fontSize: 11, color: "var(--br-text-muted)", marginTop: 10 }}>
            {payload.attentionCaveat}
          </p>
        ) : null}
      </ChartCard>
    </>
  );
};
